import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Card, CardBody, CardFooter, CardHeader, Table, Badge, Button } from 'reactstrap';
import { fetchPaymentRecord } from 'redux/Payments/PaymentsActions';
import { getPlanById } from 'config/subscriptionPlans';

const SOURCE_LABELS = {
    'trial-auto-charge': { label: 'Auto-charge', color: 'info' },
    'quick-buy': { label: 'Saved Card', color: 'primary' },
    'quick-buy-saved-card': { label: 'Saved Card', color: 'primary' },
    'quick-buy-saved-card-3ds': { label: 'Saved Card', color: 'primary' },
    'addon-quick-buy': { label: 'Add-On', color: 'success' },
    'addon-quick-buy-saved-card': { label: 'Add-On', color: 'success' },
    'addon-quick-buy-saved-card-3ds': { label: 'Add-On', color: 'success' },
    'addon-new-card': { label: 'Add-On', color: 'success' },
    'manual-subscription-purchase': { label: 'Purchase', color: 'primary' },
};

const BRAND_ICONS = {
    visa: 'fab fa-cc-visa',
    mastercard: 'fab fa-cc-mastercard',
    amex: 'fab fa-cc-amex',
    discover: 'fab fa-cc-discover',
    diners: 'fab fa-cc-diners-club',
    jcb: 'fab fa-cc-jcb',
};

const renderCard = (record) => {
    if (!record.cardLast4) {
        return <span className="text-muted">—</span>;
    }
    const iconClass = BRAND_ICONS[record.cardBrand] || 'far fa-credit-card';
    const exp = record.cardExpMonth && record.cardExpYear
        ? `${String(record.cardExpMonth).padStart(2, '0')}/${record.cardExpYear}`
        : null;
    return (
        <div className="d-flex align-items-center">
            <i className={`${iconClass} mr-2`} style={{ fontSize: '1.5rem' }} />
            <div style={{ lineHeight: 1.2 }}>
                <div>
                    <span className="text-uppercase font-weight-bold mr-1">{record.cardBrand || ''}</span>
                    <span className="text-muted">•••• {record.cardLast4}</span>
                </div>
                {exp && <small className="text-muted">Expires {exp}</small>}
            </div>
        </div>
    );
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return '—';
    }
};

const getPlanName = (record) => {
    if (record.planId) {
        const plan = getPlanById(record.planId);
        if (plan) return plan.name;
    }
    if (record.months) return `${record.months} Month${record.months !== 1 ? 's' : ''} Access`;
    return record.planId || '—';
};

const BillingHistoryCard = ({ records, loading, userHandle }) => {
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        if (userHandle) {
            dispatch(fetchPaymentRecord({ table: 'subscriptions', user: userHandle }));
        }
    }, [dispatch, userHandle]);

    // Sort most recent first
    const sorted = [...(records || [])].sort((a, b) => {
        const aDate = new Date(a.createdAt || a.subscriptionStartDate || 0).getTime();
        const bDate = new Date(b.createdAt || b.subscriptionStartDate || 0).getTime();
        return bDate - aDate;
    });

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRecords = sorted.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sorted.length / itemsPerPage);

    // Clamp currentPage if data shrinks
    useEffect(() => {
        if (currentPage > 1 && currentPage > totalPages) {
            setCurrentPage(Math.max(totalPages, 1));
        }
    }, [currentPage, totalPages]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <Card className="shadow">
            <CardHeader>
                <h3 className="mb-0">Billing History</h3>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                        <p className="text-muted">Loading billing history...</p>
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="far fa-file-alt fa-4x text-muted mb-3"></i>
                        <h3 className="text-muted">No Billing History Yet</h3>
                        <p className="text-muted mb-0">
                            Your subscription charges and auto-charges will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table className="align-items-center table-flush" hover>
                            <thead className="thead-light">
                                <tr>
                                    <th scope="col">Date</th>
                                    <th scope="col">Plan</th>
                                    <th scope="col">Amount</th>
                                    <th scope="col">Card</th>
                                    <th scope="col">Access Until</th>
                                    <th scope="col">Source</th>
                                    <th scope="col">Invoice</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentRecords.map((r, idx) => {
                                    const source = SOURCE_LABELS[r.source] || { label: 'Purchase', color: 'primary' };
                                    return (
                                        <tr key={r.docid || r.paymentId || idx}>
                                            <td>{formatDate(r.createdAt || r.subscriptionStartDate)}</td>
                                            <td>{getPlanName(r)}</td>
                                            <td className="font-weight-bold text-success">
                                                ${Number(r.amount || 0).toFixed(2)}
                                            </td>
                                            <td>{renderCard(r)}</td>
                                            <td>{formatDate(r.subscriptionEndDate)}</td>
                                            <td>
                                                <Badge color={source.color} pill>
                                                    {source.label}
                                                </Badge>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const viewUrl = r.invoiceUrl || r.receiptUrl;
                                                    if (!viewUrl) return <span className="text-muted">—</span>;
                                                    return (
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            outline
                                                            tag="a"
                                                            href={viewUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <i className="fas fa-external-link-alt mr-1" />
                                                            View
                                                        </Button>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </CardBody>
            {!loading && sorted.length > 0 && (
                <CardFooter className="py-4">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                        <div className="text-muted small mb-2 mb-sm-0">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sorted.length)} of {sorted.length} record{sorted.length !== 1 ? 's' : ''}
                        </div>
                        <div className="d-flex align-items-center">
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || totalPages <= 1}
                                className="mr-2"
                            >
                                <i className="fas fa-chevron-left"></i>
                                <span className="d-none d-sm-inline ml-1">Previous</span>
                            </Button>
                            <span className="mx-2 text-nowrap">
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages <= 1}
                                className="ml-2"
                            >
                                <span className="d-none d-sm-inline mr-1">Next</span>
                                <i className="fas fa-chevron-right"></i>
                            </Button>
                        </div>
                    </div>
                </CardFooter>
            )}
        </Card>
    );
};

const mapStateToProps = state => ({
    records: state.fetchPaymentRecord?.fetchpaymentrecord?.[0] || [],
    loading: state.fetchPaymentRecord?.loading || false,
    userHandle: state.loginAuth.userDetails?.handle,
});

export default connect(mapStateToProps)(BillingHistoryCard);
